import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CurrencyPipe } from '@angular/common';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';

import {
	ANNUAL_VOLUME_DISPLAY,
	CustomerDTO,
	DATE_YMD,
	DropdownItem,
	GET_PROJECTED_MARGIN,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	NOTIFICATION_TYPES,
	OPPORTUNITY_VALUE,
	Owner,
	ProjectDTO,
	ProjectsTable,
	RestService,
} from 'cargo-chief-commons';
import { UtilService } from '@service/util/util.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { UrlService } from '@service/url/url.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { TOOLTIP_MSG } from '@domain/constants/app-variables';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ProjectHelperService } from '@service/projectHelper/project-helper.service';
import { environment } from '@environment/environment';
import { FirebaseService } from '@service/firebase/firebase.service';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { Store } from '@ngrx/store';
import { ERROR_HANDLING } from '../../store/actions/error-handling.actions';

declare var window: any;
const NOTIFICATION_NAME = 'notification.name';

@Component({
	selector: 'app-project-panel',
	templateUrl: './project-panel.component.html',
	styleUrls: ['./project-panel.component.scss'],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [CurrencyPipe],
})
export class ProjectPanelComponent implements OnInit {
	@Input() projectsList: Array<ProjectDTO> = null;
	@Input() numberOfProjects = null;
	@Input() PAGE_SIZE: number = null;
	@Input() isLoadingUsersList: boolean = false;
	@Input() isLoadingProjectsList: boolean = false;
	@Input() isLoadingDesiredPrice: boolean = false;
	@Input() usersList: Array<Owner> = null;
	@Input() projectsViewList: Array<ProjectsTable> = null;
	@Input() PROJECT_STAGES: Array<any> = null;
	@Input() loadVolumeDuration: Array<string> = null;
	@Input() DAYS: Array<DropdownItem> = null;
	@Input() selectedProjectIDs: Array<string> = [];
	@Input() isMultiSelectEnabled: boolean = false;
	@Input() updateCustomerChange: boolean = false;
	@Input() closeCards: EventEmitter<void>;
	@Output() pageNumber = new EventEmitter<number>();
	@Output() updateSelectedProjects = new EventEmitter<any>();
	@Output() updateProjectStageId = new EventEmitter<any>();
	@Output() updateProjectsViewByClientSort = new EventEmitter<any>();
	@Output() customerDataChange = new EventEmitter<any>();
	pageIndex: number;
	step = null;
	PROJECT_POSITIONS: DropdownItem[];
	IS_NOT_EMPTY = IS_NOT_EMPTY;
	GET_PROJECTED_MARGIN = GET_PROJECTED_MARGIN;
	minimumDueDate = new Date();
	environment = environment;
	TOOLTIP_MSG = TOOLTIP_MSG;
	isEditingAnnualVolume: boolean = false;
	IS_NOT_NULL_OR_UNDEFINED = IS_NOT_NULL_OR_UNDEFINED;
	@ViewChild(MatPaginator) paginator: MatPaginator;
	private debouncer = new Subject<{
		key: string;
		project: ProjectsTable;
	}>();
	private targetMarginChanged: Subject<any> = new Subject<any>();

	constructor(
		public utilService: UtilService,
		public cargochief: Cargochief,
		private segmentWrapper: SegmentWrapperService,
		private router: Router,
		private urlService: UrlService,
		private restService: RestService,
		private activeRoute: ActivatedRoute,
		public projectHelperService: ProjectHelperService,
		private changeDetector: ChangeDetectorRef,
		private firebaseService: FirebaseService,
		public accountSettings: AccountSettingsService,
		private store: Store
	) {
		this.PROJECT_POSITIONS = this.projectHelperService.getPositions();
	}

	setStep(index: number) {
		if (!window.event.shiftKey) {
			this.step = index;
			this.changeDetector.detectChanges();
		}
	}

	ngOnInit() {
		this.activeRoute.queryParams.subscribe((params: any) => {
			this.step = null;
			this.selectedProjectIDs.length = 0;
		});

		this.closeCards?.subscribe(() => {
			this.step = null;
			this.selectedProjectIDs = [];
			this.projectsViewList = [...this.projectsViewList];
			this.changeDetector.detectChanges();
		});

		this.debouncer.pipe(debounceTime(1000)).subscribe((data: any) => {
			let projectDTO: any = this.projectsList.filter((pro) => pro.id == data.project.id)[0];
			if (!this.utilService.isNullOrUndefined(projectDTO)) {
				switch (data.key) {
					case 'minRate':
						this.maxBuyChanged(data.project);
						break;
					case 'maxRate':
						this.customerRateChanged(data.project);
						break;
				}
				this.updateProjectDTO(projectDTO, data.project, data.key);
			}
		});
		this.targetMarginChanged.pipe(debounceTime(250)).subscribe((project) => {
			this.calculateMarginChange(project);
		});
		this.firebaseService.requestPermission();
		this.firebaseService.getNotificationData().subscribe((message: any) => {
			if (
				!this.utilService.isNullOrUndefined(message) &&
				!this.utilService.isNullOrUndefined(this.projectsViewList) &&
				this.projectsViewList.length > 0
			) {
				const projectId = message['notification.projectId'];
				if (
					message[NOTIFICATION_NAME] == NOTIFICATION_TYPES.ProjectBestRateUpdatedCommand &&
					!this.utilService.isNullOrUndefined(projectId)
				) {
					this.projectsViewList.map((project) => {
						if (projectId == project.id) {
							project.bestRate = message['notification.bestRate'];
							this.changeDetector.detectChanges();
						}
					});
				}

				if (message[NOTIFICATION_NAME] == NOTIFICATION_TYPES.CalculateProbabilityCommand) {
					const projectId = message['notification.projectId'];
					if (!this.utilService.isNullOrUndefined(projectId)) {
						this.projectsViewList.map((project) => {
							if (projectId == project.id) {
								project.probability = +message['notification.probability'];
								this.changeDetector.detectChanges();
								this.updateProjectsViewByClientSort.emit();
							}
						});
					}
				}
			}
		});
	}

	inputChanged(propertyKey: string, project: ProjectsTable) {
		if (propertyKey == 'stageId') {
			this.updateProjectStageId.emit({ stageId: project.stageId, projectIds: [project.id] });
		} else {
			this.debouncer.next({
				key: propertyKey,
				project: project,
			});
		}
	}

	customerUpdated() {
		this.debouncer.next(null);
	}

	ngOnChanges() {
		let page = this.activeRoute.snapshot.queryParams['page'];
		if (
			!this.utilService.isNullOrUndefined(page) &&
			!this.utilService.isNullOrUndefined(this.paginator)
		) {
			this.paginator.pageIndex = +page;
		}
	}

	getPaginatorData(event: any) {
		if (
			!this.utilService.isNullOrUndefined(event) &&
			!this.utilService.isNullOrUndefined(event.pageIndex)
		) {
			this.pageNumber.emit(event.pageIndex);
		}
	}

	navigateToProjectDetails(project: ProjectsTable) {
		const payload = {
			user: this.utilService.getUserDataForSegmentEvent(),
			projectDTO: this.projectsList.filter((pro) => {
				return pro.id == project.id;
			})[0],
		};
		this.segmentWrapper.fireSegmentEvent(SegmentEventsConstants.user_viewed_project, payload);
		this.router.navigate([`projects/project_details/${project.id}`]);
	}

	setAssigneeId(user: Owner, project: ProjectsTable) {
		let projectDTO: ProjectDTO = this.projectsList.filter((pro) => pro.id == project.id)[0];
		projectDTO.assigneeId = this.utilService.isNullOrUndefined(user) ? null : user.userId;
		if (!this.utilService.isNullOrUndefined(projectDTO)) {
			const payload = {
				owner: user,
				user: this.utilService.getUserDataForSegmentEvent(),
				projectDTO: projectDTO,
			};
			this.segmentWrapper.fireSegmentEvent(SegmentEventsConstants.user_assigned_project, payload);
			this.updateProject(
				!this.utilService.isNullOrUndefined(user),
				'Project owner updated successfully.',
				projectDTO
			);
		}
	}

	panelClicked(project: ProjectsTable, event: MouseEvent) {
		document.getSelection().removeAllRanges();
		if (!event.shiftKey) {
			if (this.selectedProjectIDs.length > 1) {
				this.selectedProjectIDs.length = 0;
			}
			if (this.selectedProjectIDs.indexOf(project.id) > -1) {
				this.selectedProjectIDs.length = 0;
			} else {
				this.selectedProjectIDs.length = 0;
				this.selectedProjectIDs.push(project.id);
			}
		} else {
			if (this.selectedProjectIDs.indexOf(project.id) > -1) {
				const matchedIndex = this.selectedProjectIDs.indexOf(project.id);
				this.selectedProjectIDs.splice(matchedIndex, 1);
			} else {
				this.selectedProjectIDs.push(project.id);
			}
		}
		this.updateSelectedProjects.emit(this.selectedProjectIDs);
	}

	updateProjectDTO(projectDTO: any, project: ProjectsTable, propertyKey: string) {
		projectDTO = this.projectHelperService.updateProjectDTOFromView(project, projectDTO);
		projectDTO = this.projectHelperService.convertProjectDollarsToCents(projectDTO);
		projectDTO.dueDate = DATE_YMD(project.dueDateFormControl.value);
		this.updateProject(false, this.projectHelperService.getUpdateMsg(propertyKey), projectDTO);
	}

	updateProject(isOwnerAssigned: boolean, updateMsg: string, projectDTO: ProjectDTO) {
		const url = this.urlService.addProject() + `${isOwnerAssigned ? '?&isOwnerUpdated=true' : ''}`;
		this.restService.PUT(url, projectDTO).subscribe(
			(result: any) => {
				this.utilService.showSnackbar(updateMsg, SNACKBAR.SUCCESS, SNACKBAR.ACTION);
				let project: ProjectsTable = this.projectsViewList.find(
					(project) => project.id === projectDTO.id
				);
				if (this.accountSettings.ANNUAL_VOLUME) {
					if (
						IS_NOT_NULL_OR_UNDEFINED(result.data.loadVolume) &&
						IS_NOT_NULL_OR_UNDEFINED(result.data.loadVolumeDuration)
					) {
						project.annualVolumeDisplay = ANNUAL_VOLUME_DISPLAY(projectDTO);
						project.annualVolumeTooltip = `${project.loadVolume}/${projectDTO.loadVolumeDuration}`;
						project.opportunityValue = OPPORTUNITY_VALUE(projectDTO, project.annualVolumeDisplay);
					} else {
						project.annualVolumeDisplay = '';
						project.annualVolumeTooltip = '';
					}
				}
				this.changeDetector.detectChanges();
				if (this.updateCustomerChange) {
					this.customerDataChange.emit(project);
				}
			},
			(error) => {
				this.store.dispatch(
					ERROR_HANDLING.HANDLE_PROJECT_UPDATE_ERROR({
						error,
						forAdd: false,
						defaultErrorMessage: 'Unable to update project now.',
					})
				);
			}
		);
	}

	setCustomer(customer: CustomerDTO, index: number, projectId: string) {
		if (
			!this.utilService.isNullOrUndefined(customer) &&
			!this.utilService.isNullOrUndefined(customer.name)
		) {
			customer.name = customer.name.toLowerCase();
			let projectDTO: ProjectDTO,
				projectListIndex = null;
			this.projectsList.map((project, index) => {
				if (project.id == projectId) {
					projectDTO = project;
					projectListIndex = index;
				}
			});
			projectDTO.customerDTO = customer;
			projectDTO = this.projectHelperService.convertProjectDollarsToCents(projectDTO);
			this.updateProject(false, this.projectHelperService.getUpdateMsg('customer'), projectDTO);
			this.projectsViewList[index].customerDTO = this.projectsList[projectListIndex].customerDTO =
				customer;
		}
	}

	targetMarginUpdated(project: ProjectsTable) {
		if (!this.utilService.isNullOrUndefined(project)) {
			const projectDTO: ProjectDTO = this.projectsList.filter((pro) => project.id == pro.id)[0];
			if (!this.utilService.isNullOrUndefined(projectDTO)) {
				this.updateProjectDTO(projectDTO, project, 'targetMargin');
			}
		}
	}

	onTargetMarginChange(project: ProjectsTable) {
		this.targetMarginChanged.next(project);
	}

	calculateMarginChange(project: ProjectsTable) {
		const minRate = project.minRate,
			maxRate = project.maxRate;
		if (
			(this.projectHelperService.isGrossMarginValid(project.targetMargin) &&
				this.projectHelperService.isValidRateInput(minRate) &&
				this.projectHelperService.isValidRateInput(maxRate)) ||
			(this.projectHelperService.isGrossMarginValid(project.targetMargin) &&
				this.projectHelperService.isValidRateInput(maxRate))
		) {
			project.minRate = Math.round(((100 - project.targetMargin) * Number(maxRate)) / 100);
		} else if (
			this.projectHelperService.isGrossMarginValid(project.targetMargin) &&
			this.projectHelperService.isValidRateInput(minRate)
		) {
			project.maxRate = Math.round((Number(minRate) * 100) / (100 - Number(project.targetMargin)));
		}
	}

	maxBuyChanged(project: ProjectsTable) {
		const minRate = project.minRate,
			maxRate = project.maxRate,
			currentMargin = project.targetMargin;
		if (
			this.projectHelperService.isGrossMarginValid(currentMargin) &&
			this.projectHelperService.isValidRateInput(minRate) &&
			this.projectHelperService.isValidRateInput(maxRate)
		) {
			project.maxRate = Math.round((Number(minRate) * 100) / (100 - project.targetMargin));
		} else if (
			!this.projectHelperService.isGrossMarginValid(currentMargin) &&
			this.projectHelperService.isValidRateInput(minRate) &&
			this.projectHelperService.isValidRateInput(maxRate)
		) {
			const calculatedMargin = ((Number(maxRate) - Number(minRate)) / Number(maxRate)) * 100;
			if (calculatedMargin > 0) {
				project.targetMargin = Math.round(Number(calculatedMargin));
			}
		}
	}

	customerRateChanged(project: ProjectsTable) {
		const minRate = project.minRate,
			maxRate = project.maxRate,
			currentMargin = project.targetMargin;
		if (
			this.projectHelperService.isGrossMarginValid(currentMargin) &&
			this.projectHelperService.isValidRateInput(minRate) &&
			this.projectHelperService.isValidRateInput(maxRate)
		) {
			project.minRate = Math.round(((100 - project.targetMargin) * Number(maxRate)) / 100);
		} else if (
			!this.projectHelperService.isGrossMarginValid(currentMargin) &&
			this.projectHelperService.isValidRateInput(minRate) &&
			this.projectHelperService.isValidRateInput(maxRate)
		) {
			const calculatedMargin = ((Number(maxRate) - Number(minRate)) / Number(maxRate)) * 100;
			if (calculatedMargin > 0) {
				project.targetMargin = Math.round(Number(calculatedMargin));
			}
		}
	}

	async submitLastBid(project: ProjectsTable) {
		if (!this.utilService.isNullOrUndefined(project)) {
			const projectDTO: ProjectDTO = this.projectsList.filter((pro) => project.id == pro.id)[0];
			if (!this.utilService.isNullOrUndefined(projectDTO)) {
				let result = await this.projectHelperService.openLastBidDialog();
				if (!this.utilService.isNullOrUndefined(result)) {
					project.lastBid = result.lastBid;
					this.updateProjectDTO(projectDTO, project, 'lastBid');
				}
			}
		}
	}

	getProjectDTOById(projectId: string): ProjectDTO {
		return this.projectsList.filter((project) => project.id == projectId)[0];
	}

	getProbabilityLabelColor(label: string): any {
		if (IS_NOT_EMPTY(label)) {
			switch (label) {
				case this.projectHelperService.PROBABILITY_LABELS.MEDIUM:
					return { color: '#D3A65E' };
				case this.projectHelperService.PROBABILITY_LABELS.HIGH:
					return { color: '#CF6666' };
				case this.projectHelperService.PROBABILITY_LABELS.LOW:
					return { color: '#90AEC8' };
			}
		}
	}

	openCard(index: number) {
		if (
			IS_NULL_OR_UNDEFINED(this.step) ||
			(IS_NOT_NULL_OR_UNDEFINED(this.step) && this.step !== index)
		) {
			this.setStep(index);
		} else {
			this.step = null;
			this.changeDetector.detectChanges();
		}
	}

	rfpCompleted(data: any, projectId: string) {
		if (IS_NOT_NULL_OR_UNDEFINED(data) && IS_NOT_EMPTY(projectId)) {
			const project = this.projectsList.find((project) => project.id == projectId);
			if (IS_NOT_NULL_OR_UNDEFINED(project)) {
				project.rfpRequestedDate = new Date();
			}
		}
	}
}
